
import { useMemo } from 'react';
import { CareerPath } from '@/types/resultsTypes';
import { useLanguage } from '@/contexts/LanguageContext';

export const useCareerPaths = (scores: {
  dominance: number;
  extraversion: number;
  patience: number;
  formality: number;
}, objectivityScore: number): CareerPath[] => {
  const { t } = useLanguage();

  return useMemo(() => {
    const paths: CareerPath[] = [];

    if (scores.dominance > 0.5) {
      if (scores.extraversion > 0.5) {
        paths.push({
          category: t('career.executiveLeadership') || 'Executive Leadership',
          roles: ['CEO/Executive', 'Sales Director', 'Marketing Leader', 'Business Development'],
          description: t('career.executiveLeadership.description') || 'Leadership roles requiring both authority and people skills'
        });
      } else {
        paths.push({
          category: t('career.strategicLeadership') || 'Strategic Leadership',
          roles: ['Operations Director', 'Project Manager', 'Strategy Consultant', 'Department Head'],
          description: t('career.strategicLeadership.description') || 'Leadership focused on strategy and execution'
        });
      }
    }

    if (scores.extraversion > 0.5) {
      paths.push({
        category: t('career.peopleFocused') || 'People-Focused Roles',
        roles: ['Human Resources', 'Training & Development', 'Customer Success', 'Team Lead'],
        description: t('career.peopleFocused.description') || 'Roles emphasizing interpersonal skills and team dynamics'
      });
    }

    if (scores.patience > 0.5 || scores.formality > 0.5) {
      paths.push({
        category: t('career.systemsProcess') || 'Systems & Process',
        roles: ['Operations Manager', 'Quality Assurance', 'Process Analyst', 'Compliance Officer'],
        description: t('career.systemsProcess.description') || 'Roles requiring systematic approach and attention to detail'
      });
    }

    if (scores.patience < -0.5 || scores.formality < -0.5) {
      paths.push({
        category: t('career.innovationChange') || 'Innovation & Change',
        roles: ['Entrepreneur', 'Product Manager', 'Change Management', 'Startup Founder'],
        description: t('career.innovationChange.description') || 'Dynamic roles driving innovation and transformation'
      });
    }

    if (objectivityScore > 0.5) {
      paths.push({
        category: t('career.analyticalResearch') || 'Analytical & Research',
        roles: ['Data Analyst', 'Research Scientist', 'Financial Analyst', 'Management Consultant'],
        description: t('career.analyticalResearch.description') || 'Roles requiring analytical thinking and research skills'
      });
    }

    return paths.length > 0 ? paths : [{
      category: t('career.versatileProfessional') || 'Versatile Professional',
      roles: ['Consultant', 'Project Coordinator', 'Business Analyst', 'Team Member'],
      description: t('career.versatileProfessional.description') || 'Balanced professional roles across various industries'
    }];
  }, [scores, objectivityScore, t]);
};
